import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSystemStatus } from '../../hooks/useSystemStatus';
import { useMode } from '../../hooks/useMode';
import { StatusBadge } from '../cards/StatusBadge';
import { cn } from '../../lib/utils';

function formatCooldownRemaining(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return '';
  const min = Math.ceil(ms / 60_000);
  return `${min}m`;
}

export function StatusStrip() {
  const { data: status } = useSystemStatus();
  const { mode } = useMode();
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!status?.activeCooldownExpiresAt) return;
    const interval = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(interval);
  }, [status?.activeCooldownExpiresAt]);

  if (!status) return null;

  const cooldownRemaining = status.activeCooldownExpiresAt
    ? formatCooldownRemaining(status.activeCooldownExpiresAt)
    : '';

  return (
    <div
      className="flex flex-wrap items-center gap-3 px-3 py-2.5 border-b bg-muted/30 text-sm sm:gap-4 sm:px-4"
      role="status"
      aria-label="System status"
    >
      <span className="flex items-center gap-2">
        <span className="text-muted-foreground">System:</span>
        <StatusBadge status={status.mode} />
      </span>
      <span className="flex items-center gap-2">
        <span className="text-muted-foreground">Risk:</span>
        <StatusBadge status={status.killSwitch} />
      </span>
      <span className="flex items-center gap-2">
        <span className="text-muted-foreground">Interface:</span>
        <span className="font-medium">{mode === 'beginner' ? 'Beginner' : 'Advanced'}</span>
      </span>
      <span className="flex items-center gap-2">
        <span className="text-muted-foreground">Exchange:</span>
        <span
          className={cn(
            'font-medium',
            status.exchangeConnected ? 'text-success' : 'text-warning'
          )}
          aria-label={status.exchangeConnected ? 'Exchange connected' : 'Exchange disconnected'}
        >
          {status.exchangeConnected ? 'Connected' : 'Disconnected'}
        </span>
      </span>

      {status.blackoutActive && (
        <span
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30"
          title={`Blackout hours (UTC): ${(status.blackoutHoursUtc ?? []).join(', ')}`}
        >
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="font-medium text-amber-600 dark:text-amber-400">Blackout</span>
        </span>
      )}

      {cooldownRemaining && status.activeCooldownSymbol && (
        <span
          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-500/30"
          title={`Cooldown on ${status.activeCooldownSymbol} expires at ${status.activeCooldownExpiresAt}`}
        >
          <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
          <span className="font-medium text-blue-600 dark:text-blue-400">
            Cooldown {status.activeCooldownSymbol} {cooldownRemaining}
          </span>
        </span>
      )}

      {typeof status.activeAlertsCount === 'number' && status.activeAlertsCount > 0 && (
        <Link
          to="/alerts"
          className="flex items-center gap-2 text-warning hover:underline font-medium"
          aria-label={`${status.activeAlertsCount} alerts need attention`}
        >
          <span className="text-muted-foreground">Alerts:</span>
          <span className="font-medium">{status.activeAlertsCount}</span>
        </Link>
      )}
    </div>
  );
}
